import React, { memo } from 'react';
import ConversationAvatar from './ConversationAvatar';
import Icon from '../AppIcon';

const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';
  const numeric = Number(timestamp);
  const messageTime = Number.isFinite(numeric) ? new Date(numeric) : new Date(timestamp);
  if (isNaN(messageTime.getTime())) return '';
  const now = new Date();
  const diffInMinutes = Math.floor((now - messageTime) / (1000 * 60));
  if (diffInMinutes < 1) return 'Just now';
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
  if (diffInMinutes < 10080) return `${Math.floor(diffInMinutes / 1440)}d ago`;
  return messageTime.toLocaleDateString();
};

/**
 * Memoized conversation list item for the sidebar.
 * Extracted from UnifiedMessagingPage to prevent remount on every parent render.
 */
const ConversationListItem = memo(({ conversation, isActive, currentUserId, isEmployer, onSelect }) => {
  const recipientName = conversation.participant_name;
  const otherUserId = conversation.participant_1 === currentUserId
    ? conversation.participant_2
    : conversation.participant_1;

  return (
    <div
      onClick={() => onSelect(conversation)}
      className={`p-3 sm:p-4 cursor-pointer hover:bg-gray-50 active:bg-gray-100 transition-colors min-h-[88px] flex items-start ${
        isActive ? 'bg-blue-50 lg:border-r-2 border-blue-500' : ''
      }`}
    >
      <div className="flex items-start space-x-3">
        <ConversationAvatar
          profilePicture={conversation.participant_avatar}
          name={recipientName}
          userId={otherUserId}
          showOnlineStatus={true}
          size="w-10 h-10 sm:w-12 sm:h-12"
        />

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-1">
            <h3 className="font-semibold text-sm sm:text-base text-gray-900 truncate pr-2">
              {recipientName}
            </h3>
            <div className="flex items-center space-x-1 sm:space-x-2 flex-shrink-0">
              {conversation.unread_count > 0 && (
                <span className="bg-blue-500 text-white text-xs font-bold px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full">
                  {conversation.unread_count}
                </span>
              )}
              <span className="text-xs text-gray-500 whitespace-nowrap">
                {formatTimestamp(conversation.last_message_at)}
              </span>
            </div>
          </div>

          {/* Job context with merged indicator */}
          <div className="text-xs sm:text-sm text-gray-600 mb-1 line-clamp-1 flex items-center gap-2">
            <span className="truncate">
              {isEmployer
                ? conversation.job_context
                : conversation.company_name && conversation.job_title
                  ? `${conversation.company_name} • ${conversation.job_title}`
                  : conversation.company_name || conversation.job_title || ''
              }            </span>
            {conversation.was_merged && (
              <div className="relative inline-block group flex-shrink-0">
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  <Icon name="GitMerge" size={12} className="mr-1" />
                  Merged
                </span>
                <div className="absolute z-10 invisible group-hover:visible bg-gray-800 text-white text-xs rounded py-1 px-2 right-0 bottom-full mb-1 w-48">
                  This conversation combines messages from multiple threads
                  <svg className="absolute text-gray-800 h-2 w-full left-0 top-full" x="0px" y="0px" viewBox="0 0 255 255"><polygon className="fill-current" points="0,0 127.5,127.5 255,0"></polygon></svg>
                </div>
              </div>
            )}
          </div>

          {/* Last message preview */}
          <p className="text-xs sm:text-sm text-gray-700 line-clamp-1">
            {conversation.last_sender_id === currentUserId && (
              <span className="text-gray-500">You: </span>
            )}
            {typeof conversation.last_message === 'string'
              ? conversation.last_message.includes('📅 Interview Scheduled')
                ? '📅 Interview scheduled'
                : conversation.last_message
              : ''}
          </p>
        </div>
      </div>
    </div>
  );
});

ConversationListItem.displayName = 'ConversationListItem';
export default ConversationListItem;
